import { createSlice } from '@reduxjs/toolkit';
import { enhancedCartApi as cartApi, mapCartItems } from '../apis/endpoints/cartApi';

// Initial state
const initialState = {
  cartItems: [], // Unified cart items
  loading: false,
  error: null,
  addCartSuccess: true, // Kept for potential individual item feedback
  cartUpdating: false,
};

// Transaction types
export const TRANSACTION_TYPES = {
  BUY: 'buy',
  RENT: 'rent',
  SWAP: 'swap'
};


const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    setCartLoading: (state) => {
      state.loading = true;
      state.error = null;
    },
    setCartUpdating: (state, action) => {
      state.cartUpdating = action.payload;
    },
    setCartError: (state, action) => {
      state.loading = false;
      state.cartUpdating = false;
      state.error = action.payload;
      state.addCartSuccess = false; // Generic success flag
    },
    setCartItems: (state, action) => {
      state.loading = false;
      state.error = null;
      state.cartItems = action.payload;
    },
    setAddCartSuccess: (state, action) => { // Generic success flag
      state.addCartSuccess = action.payload;
      state.cartUpdating = false;
    },
    updateCartItemDeliveryDetails: (state, action) => {
      const { itemIdentifier, deliveryMethod, selectedShippoRateId, pickupAddressId, cartItemIndexUsed } = action.payload;

      const itemIndex = cartItemIndexUsed 
        ? state.cartItems.findIndex(item => item.cartItemIndex === itemIdentifier)
        : state.cartItems.findIndex(item => item.id === itemIdentifier);

      if (itemIndex !== -1) {
        const currentItem = state.cartItems[itemIndex];
        state.cartItems[itemIndex] = {
          ...currentItem,
          deliveryMethod,
          selectedShippoRateId: deliveryMethod === 'shipping' ? selectedShippoRateId : null,
          pickupAddressId: deliveryMethod === 'pickup' ? pickupAddressId : null,
        };
        
        if (deliveryMethod === 'shipping') {
          state.cartItems[itemIndex].pickupAddressId = null;
        } else if (deliveryMethod === 'pickup') {
          state.cartItems[itemIndex].selectedShippoRateId = null;
        }
      }
    },
    // New reducer for optimistic quantity updates
    updateCartItemQuantity: (state, action) => {
      const { cartItemId, cartItemIndex, newQuantity } = action.payload;
      
      // Find the item to update
      let itemIndex = -1;
      if (cartItemId) {
        itemIndex = state.cartItems.findIndex(item => item.cartItemId === cartItemId);
      } else if (cartItemIndex !== undefined) {
        itemIndex = state.cartItems.findIndex(item => item.cartItemIndex === cartItemIndex);
      }

      if (itemIndex !== -1) {
        // Optimistically update the quantity in local state
        state.cartItems[itemIndex].quantity = newQuantity;
      }
    },
    clearCartState: (state) => { // Renamed to avoid conflict with API call, clears only local state
      state.cartItems = [];
      state.loading = false;
      state.error = null;
      state.cartUpdating = false;
      // Reset other relevant flags if needed
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        cartApi.endpoints.getCart.matchFulfilled,
        (state, { payload }) => {
          state.cartItems = mapCartItems(payload); // mapCartItems should be updated
          state.loading = false;
          state.error = null;
        }
      )
      .addMatcher(
        cartApi.endpoints.getCart.matchPending,
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )
      .addMatcher(
        cartApi.endpoints.getCart.matchRejected,
        (state, { error }) => {
          state.loading = false;
          state.error = error.message || 'Failed to fetch cart';
        }
      )
      .addMatcher(
        cartApi.endpoints.addCartItem.matchFulfilled,
        (state) => {
          state.cartUpdating = false;
          state.addCartSuccess = true; // Generic success
          state.error = null;
          // Backend returns the whole cart, so getCart will be refetched due to invalidation.
        }
      )
      .addMatcher(
        cartApi.endpoints.addCartItem.matchPending,
        (state) => {
          state.cartUpdating = true;
        }
      )
      .addMatcher(
        cartApi.endpoints.addCartItem.matchRejected,
        (state, { error }) => {
          state.cartUpdating = false;
          state.addCartSuccess = false;
          state.error = error.message || 'Failed to add item to cart';
        }
      )
      .addMatcher(
        cartApi.endpoints.removeCartItem.matchFulfilled,
        (state) => {
          state.cartUpdating = false;
          state.error = null;
          // Refetch handled by invalidation
        }
      )
      .addMatcher(
        cartApi.endpoints.removeCartItem.matchPending,
        (state) => {
          state.cartUpdating = true;
        }
      )
      .addMatcher(
        cartApi.endpoints.removeCartItem.matchRejected,
        (state, { error }) => {
          state.cartUpdating = false;
          state.error = error.message || 'Failed to remove item from cart';
        }
      )
      .addMatcher(
        cartApi.endpoints.updateCartItem.matchFulfilled,
        (state) => {
          state.cartUpdating = false;
          state.error = null;
          // Refetch handled by invalidation
        }
      )
      .addMatcher(
        cartApi.endpoints.updateCartItem.matchPending,
        (state) => {
          state.cartUpdating = true;
        }
      )
      .addMatcher(
        cartApi.endpoints.updateCartItem.matchRejected,
        (state, { error }) => {
          state.cartUpdating = false;
          state.error = error.message || 'Failed to update item in cart';
        }
      )
      .addMatcher(
        cartApi.endpoints.replaceRentalPeriod.matchFulfilled,
        (state) => {
          state.cartUpdating = false;
          state.error = null;
          // Refetch handled by invalidation
        }
      )
      .addMatcher(
        cartApi.endpoints.replaceRentalPeriod.matchPending,
        (state) => {
          state.cartUpdating = true;
        }
      )
      .addMatcher(
        cartApi.endpoints.replaceRentalPeriod.matchRejected,
        (state, { error }) => {
          state.cartUpdating = false;
          state.error = error.message || 'Failed to update rental period';
        }
      )
      .addMatcher(
        cartApi.endpoints.clearCart.matchFulfilled, // This is API call to clear on backend
        (state) => {
          state.cartItems = []; // Clear local items as well
          state.cartUpdating = false;
          state.error = null;
        }
      );
  }
});

export const {
  setCartLoading,
  setCartError,
  setCartItems, 
  setAddCartSuccess,
  clearCartState, // For local state clearing
  setCartUpdating,
  updateCartItemDeliveryDetails,
  updateCartItemQuantity, // Export the new reducer
} = cartSlice.actions;

// Selectors
export const selectAllCartItems = state => state.cart?.cartItems || [];
export const selectCartLoading = state => state.cart?.loading || false;
export const selectCartUpdating = state => state.cart?.cartUpdating || false;
export const selectCartError = state => state.cart?.error || null;
export const selectAddCartSuccess = state => state.cart?.addCartSuccess || true; // Generic

// Selector to group items by currency, then by merchant + delivery + item type
export const selectCartItemsByCurrencyAndMerchant = (state) => {
  const items = state.cart?.cartItems || [];
  const groupedByCurrency = items.reduce((acc, item) => {
    const currency = (item.currency || 'USD').toUpperCase();
    if (!acc[currency]) {
      acc[currency] = [];
    }
    acc[currency].push(item);
    return acc;
  }, {});

  const finalGroup = {};
  for (const currency in groupedByCurrency) {
    finalGroup[currency] = groupedByCurrency[currency].reduce((acc, item) => {
      const merchantId = item.merchant?.id || 'unknown_merchant';
      const deliveryMethod = item.deliveryMethod || 'shipping';
      const itemType = item.type || 'buy';
      const groupKey = `${merchantId}-${deliveryMethod}-${itemType}`;

      if (!acc[groupKey]) {
        acc[groupKey] = {
          merchantId: item.merchant?.id,
          merchantName: item.merchant?.username || 'Unknown Merchant',
          deliveryMethod: deliveryMethod,
          itemType: itemType,
          products: []
        };
      }
      acc[groupKey].products.push(item);
      return acc;
    }, {});
  }
  return finalGroup;
};

// Selector to get unique currencies in the cart
export const selectCartCurrencies = (state) => {
  const items = state.cart?.cartItems || [];
  const currencies = new Set(items.map(item => (item.currency || 'USD').toUpperCase()));
  return Array.from(currencies);
};


// Helper function to create cart item (remains largely the same)
export const createCartItem = ({
  productId,
  quantity = 1,
  type = TRANSACTION_TYPES.BUY, // type is crucial
  productOptions = [],
  customNote = '',
  merchant = null,
  rentalPeriod = null // Stays as rentalPeriod for backend compatibility
}) => {
  const cartItem = {
    product: productId,
    quantity,
    type,
    productOptions,
    customNote
  };
  
  if (merchant) {
    cartItem.merchant = merchant;
  }
  
  // rentalPeriod is only added if type is 'rent'
  if (type === TRANSACTION_TYPES.RENT && rentalPeriod) {
    cartItem.rentalPeriod = rentalPeriod;
  }
  
  return cartItem;
};

// Thunks (largely remain the same as they call API endpoints, and API handles cart structure)
// fetchCartItems, addToCart, updateCartItem, removeFromCart, emptyCart (API), checkoutCart

export const fetchCartItems = () => async (dispatch) => {
  try {
    dispatch(setCartLoading());
    // No need to check token here, baseApi will handle it or error will be caught
    const resultAction = await dispatch(cartApi.endpoints.getCart.initiate());
    if (resultAction.error) {
      dispatch(setCartError(resultAction.error.data?.message || resultAction.error.message || 'Failed to load cart'));
    }
    // Data is handled by the matcher
  } catch (error) {
    dispatch(setCartError(error.message || 'An unexpected error occurred while fetching cart'));
  }
};

export const addToCart = (cartItemData) => async (dispatch) => {
  try {
    dispatch(setCartUpdating(true));
    const cartItem = createCartItem(cartItemData); // Uses the updated createCartItem
    const resultAction = await dispatch(cartApi.endpoints.addCartItem.initiate(cartItem));
    if (resultAction.error) {
      dispatch(setCartError(resultAction.error.data?.message || resultAction.error.message || 'Failed to add item'));
      dispatch(setAddCartSuccess(false));
    } else {
      dispatch(setAddCartSuccess(true));
      // Invalidation will refetch cart
    }
  } catch (error) {
    dispatch(setCartError(error.message || 'An unexpected error occurred while adding to cart'));
    dispatch(setAddCartSuccess(false));
  }
};

export const updateCartItem = (itemIndex, updates) => async (dispatch) => {
  try {
    dispatch(setCartUpdating(true));
    const resultAction = await dispatch(cartApi.endpoints.updateCartItem.initiate({ itemIndex, cartItem: updates }));
     if (resultAction.error) {
      dispatch(setCartError(resultAction.error.data?.message || resultAction.error.message || 'Failed to update item'));
    }
    // Invalidation will refetch cart
  } catch (error) {
    dispatch(setCartError(error.message || 'An unexpected error occurred while updating cart item'));
  }
};

export const removeFromCart = (cartItemId) => async (dispatch) => {
  try {
    dispatch(setCartUpdating(true));
    const resultAction = await dispatch(cartApi.endpoints.removeCartItem.initiate(cartItemId));
    if (resultAction.error) {
      dispatch(setCartError(resultAction.error.data?.message || resultAction.error.message || 'Failed to remove item'));
    }
    // Invalidation will refetch cart
  } catch (error) {
    dispatch(setCartError(error.message || 'An unexpected error occurred while removing item'));
  }
};

// Thunk to clear cart on the backend AND locally
export const clearBackendAndLocalCart = () => async (dispatch) => {
  try {
    dispatch(setCartUpdating(true));
    const resultAction = await dispatch(cartApi.endpoints.clearCart.initiate()); // Clears backend cart
    if (resultAction.error) {
      dispatch(setCartError(resultAction.error.data?.message || resultAction.error.message || 'Failed to clear cart on server'));
    } else {
      dispatch(clearCartState()); // Clears local cart items
    }
  } catch (error) {
    dispatch(setCartError(error.message || 'An unexpected error occurred while clearing cart'));
  }
};


export const checkoutCart = (checkoutData) => async (dispatch) => {
  try {
    dispatch(setCartUpdating(true));
    const result = await dispatch(cartApi.endpoints.checkout.initiate(checkoutData)).unwrap();
    // Assuming checkout implies cart is cleared or items are removed server-side,
    // and getCart refetch will update local state.
    // Or, dispatch clearCartState() if checkout always means local cart is empty.
    // For now, relying on refetch.
    return result;
  } catch (error) {
    dispatch(setCartError(error.data?.message || error.message || 'Checkout failed'));
    throw error;
  }
};

export const updateCartItems = ({ cartItemId, cartItemIndex, action }) => async (dispatch, getState) => {
  try {
    dispatch(setCartUpdating(true));
    
    const currentItems = getState().cart.cartItems;
    // Finding item by cartItemId (which is the component ID from backend) is more robust
    // If cartItemId is not available, then fall back to cartItemIndex.
    const currentItem = cartItemId 
      ? currentItems.find(item => item.cartItemId === cartItemId)
      : currentItems[cartItemIndex];

    if (!currentItem) {
      dispatch(setCartError('Item not found in cart for update.'));
      dispatch(setCartUpdating(false));
      return;
    }
    
    // Use currentItem.cartItemIndex for the API call as it expects the index in the cartItems array on backend
    const backendItemIndex = currentItem.cartItemIndex; 

    if (action === 'delete') {
      if (!currentItem.cartItemId) {
        dispatch(setCartError('Cannot delete item: Item component ID missing.'));
        dispatch(setCartUpdating(false));
        return;
      }
      // Backend expects the unique ID of the cartItem component
      await dispatch(cartApi.endpoints.removeCartItem.initiate(currentItem.cartItemId));
      return; // removeCartItem invalidates 'Cart' tag, triggering refetch
    }
    
    const currentQuantity = currentItem.quantity || 1;
    const newQuantity = action === 'increase' ? currentQuantity + 1 : Math.max(1, currentQuantity - 1);
    
    // Stock check before optimistic update
    const stock = parseInt(currentItem.stock, 10);
    if (action === 'increase' && !isNaN(stock) && newQuantity > stock) {
      dispatch(setCartError(`Cannot add more ${currentItem.name}. Only ${stock} available.`));
      dispatch(setCartUpdating(false));
      return;
    }
    
    // Optimistically update the UI immediately
    dispatch(updateCartItemQuantity({ cartItemId, cartItemIndex, newQuantity }));
    
    // Create update object with quantity
    const updateData = { quantity: newQuantity };
    
    // If this is a rental item, we may need to include the rental period
    // to satisfy backend validation requirements
    if (currentItem.type === 'rent' && currentItem.rentalPeriod) {
      // Only include rental period if we're just updating quantity
      updateData.rentalPeriod = {
        ...currentItem.rentalPeriod
      };
      
      // The backend controller checks for these dates if type is 'rent'
      // If they're missing, we'll add fake ones just to make the validation pass
      if (action === 'increase' || action === 'decrease') {
        // Only add placeholder dates if they're required for validation but missing
        if (!updateData.rentalPeriod.startDate || !updateData.rentalPeriod.endDate) {
          const today = new Date();
          const endDate = new Date();
          
          // Calculate end date based on rental period
          if (updateData.rentalPeriod.duration) {
            endDate.setDate(today.getDate() + updateData.rentalPeriod.duration);
          } else if (updateData.rentalPeriod.option && updateData.rentalPeriod.number) {
            if (updateData.rentalPeriod.option === 'day') {
              endDate.setDate(endDate.getDate() + updateData.rentalPeriod.number);
            } else if (updateData.rentalPeriod.option === 'week') {
              endDate.setDate(endDate.getDate() + updateData.rentalPeriod.number * 7);
            } else if (updateData.rentalPeriod.option === 'month') {
              endDate.setMonth(endDate.getMonth() + updateData.rentalPeriod.number);
            }
          } else {
            // Default is 1 day if we can't calculate
            endDate.setDate(today.getDate() + 1);
          }
          
          // Add the dates to the rental period
          updateData.rentalPeriod.startDate = today.toISOString();
          updateData.rentalPeriod.endDate = endDate.toISOString();
        }
      }
    }
    
    try {
      // API expects the index of the item in the backend's cartItems array
    await dispatch(cartApi.endpoints.updateCartItem.initiate({
        itemIndex: backendItemIndex, 
        cartItem: updateData
      }));
      // updateCartItem invalidates 'Cart' tag, triggering refetch
    } catch (apiError) {
      // If API call fails, revert the optimistic update
      if (currentItem) {
        dispatch(updateCartItemQuantity({ 
          cartItemId, 
          cartItemIndex, 
          newQuantity: currentQuantity  // Revert to original quantity
        }));
      }
      throw apiError; // Re-throw to be caught by outer try/catch
    }
  } catch (error) {
    dispatch(setCartError(error.message || 'Failed to update cart item quantity'));
  } finally {
    dispatch(setCartUpdating(false));
  }
};

export default cartSlice.reducer; 